import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaCartShopping } from "react-icons/fa6";
import { FaShareAlt } from "react-icons/fa";
import LikeButton from "./LikeButton";
import Download from "../component/download.js";
import ShareModal from "./ShareModal";
import MoreOptions from "./moreOptions.js";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import djImage from '../../images/dj.jpg';
import { trackEvent } from "../../App"; // adjust path if needed
import { useUserLocation } from "../utils/useUserLocation";
import { getExchangeRate } from "../utils/exchangeRate";
import { createSlug } from "../utils/slugify";
import {query, where } from "firebase/firestore";

function SongList({ songs, playSong, selectedSong, setSelectedSong }) {
  const [displayedSongs, setDisplayedSongs] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const db = getFirestore();
  const [selectedGenres, setSelectedGenres] = useState([]);

  // Location & exchange rate logic
  const userCountry = useUserLocation();
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    if (userCountry === "NG") {
      async function fetchRate() {
        try {
          const rate = await getExchangeRate();
          setExchangeRate(rate);
        } catch {
          setExchangeRate(null);
        }
      }
      fetchRate();
    }
  }, [userCountry]);

  const parsePrice = (price) => {
    if (!price) return 0;
    const num = parseFloat(price.toString().replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const formatPrice = (usdAmount) => {
    if (!usdAmount) usdAmount = 0;
    if (userCountry === "NG" && exchangeRate) {
      return `₦${Math.round(usdAmount * exchangeRate).toLocaleString()}`;
    }
    return `$${usdAmount.toFixed(2)}`;
  };

  useEffect(() => {
    const fetchLikes = async () => {
      const songsWithLikes = await Promise.all(
        songs.map(async (song) => {
          const likesRef = collection(db, `beats/${song.id}/likes`);
          const likesSnapshot = await getDocs(likesRef);
          return { ...song, likes: likesSnapshot.size };
        })
      );

      // Sort songs by likes in descending order and then shuffle
      const sortedSongs = songsWithLikes.sort((a, b) => b.likes - a.likes);
      const shuffledSongs = sortedSongs.sort(() => 0.5 - Math.random());

      setDisplayedSongs(shuffledSongs.slice(0, 20));
    };

    fetchLikes();
  }, [songs, db]);

  const lastSongElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setDisplayedSongs((prevSongs) => {
            const newSongs = songs.slice(prevSongs.length, prevSongs.length + 20);
            if (newSongs.length === 0) {
              setHasMore(false);
            }
            return [...prevSongs, ...newSongs];
          });
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, songs]
  );

  const openShareModal = (song, event) => {
    event.stopPropagation(); // Prevent triggering play on click
    setSelectedSong(song);
  };

  // genre filter: support multiple selection
  const handleGenreClick = async (genre) => {
    let updatedGenres = [];
    if (selectedGenres.includes(genre)) {
      updatedGenres = selectedGenres.filter((g) => g !== genre);
    } else {
      updatedGenres = [...selectedGenres, genre];
    }
    setSelectedGenres(updatedGenres);

    if (updatedGenres.length === 0) {
      // If no genres selected, show default (shuffled/sorted) songs
      const songsWithLikes = await Promise.all(
        songs.map(async (song) => {
          const likesRef = collection(db, `beats/${song.id}/likes`);
          const likesSnapshot = await getDocs(likesRef);
          return { ...song, likes: likesSnapshot.size };
        })
      );
      const sortedSongs = songsWithLikes.sort((a, b) => b.likes - a.likes);
      const shuffledSongs = sortedSongs.sort(() => 0.5 - Math.random());
      setDisplayedSongs(shuffledSongs.slice(0, 20));
      return;
    }

    try {
      const allFilteredSongs = [];
      for (const g of updatedGenres) {
        const q = query(collection(db, "beats"), where("metadata.genres", "==", g));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          allFilteredSongs.push({ id: doc.id, ...doc.data() });
        });
      }
      // Remove duplicates
      const uniqueSongs = allFilteredSongs.filter(
        (song, index, self) => index === self.findIndex((s) => s.id === song.id)
      );
      setDisplayedSongs(uniqueSongs);
    } catch (error) {
      console.error("Error fetching songs by genre:", error);
    }
  };


  return (
    <div className="GroupC2">
      <div className="genre-title">
  {selectedGenres.length === 0
    ? "Top Genres"
    : selectedGenres.length === 1
      ? selectedGenres[0]
      : `${selectedGenres.length} genres`}
</div>
     <div className="genre-filter">
  {[ "RnB", "Trap", "Hip Hop", "PoP","Rap","Reggae", "Afro beat"].map((genre) => (
    <button
      key={genre}
      onClick={() => handleGenreClick(genre)}
      className={selectedGenres.includes(genre) ? "active-genre" : ""}
    >
      {genre}
    </button>
  ))}
</div>
      <div className="songcontainer">
        {displayedSongs.map((song, index) => (
          <div
            className="songlist"
            key={song.id}
            onClick={() => {
              const songIndex = songs.findIndex((s) => s.id === song.id);
              if (songIndex === -1 || !song.musicUrls?.taggedMp3) {
                console.error("Invalid song or missing audio URL:", song.title || "Untitled");
                return;
              }
              // Track play event in GA
  trackEvent({
    eventName: "play_song",
    songTitle: song.title,
    artist: song.username,
  });
              playSong(songIndex);
            }}
            ref={index === displayedSongs.length - 1 ? lastSongElementRef : null}  
            >
            <img
              src={song.coverUrl || djImage}
              className="listimage"
              alt={song.title || "Untitled"}
            />
            <div className="songListTitle">
              {song.title || "Unknown Title"}
              <div style={{ fontSize: "0.9em" }}>
              {song.username || ""}
              <LikeButton songId={song.id} />
              </div>
            </div>

            <div className="market">
              <div className="songlist-taglist">
              {song.metadata?.tags?.map((tag, index) => (
                <span key={index} className="songlist-tag">
                {tag.trim()}
                </span>
              ))}
              </div>

              
<Link to={`/addToCart/${createSlug(song.title, song.id)}`} state={{ song }}>
              <button className="songlist-addtochart">
                <FaCartShopping style={{ marginRight: "6px" }} />
                {formatPrice(parsePrice(song.monetization?.basic?.price))}
              </button>
              </Link>

              <MoreOptions song={song} openShareModal={openShareModal} className="MoreOptions-" />
            </div>
            </div>
          ))}
          </div>

          {/* Share Modal */}
      {selectedSong && (
        <ShareModal song={selectedSong} onClose={() => setSelectedSong(null)} />
      )}
    </div>
  );
}

export default SongList;
